// apps/web/src/features/russia-requests/components/tabs/CargoTab.tsx
//
// #1548 lean-drawer cargo section. Folds the old InfoTab header
// (type / status / name / start cities / end address) above a kind-aware
// cargo body:
//   - SIMPLE  → the read-only positions table (reuses `ItemsTab`).
//   - COMPLEX → request-level total weight / volume + the read-only file
//     list (#1546 surface; READ only — no upload here).
//
// @aiPurpose pure read view; no mutations. The drawer owns transitions.

import { useTranslation } from 'react-i18next';
import { RussiaRequestKind, type RussiaRequest } from '@magimp/shared';

import { RequestStatusPill } from '../../../requests/display/index.js';
import { RussiaRequestTypeChip } from '../RussiaRequestTypeChip.js';
import { StartCityChips } from '../StartCityChips.js';
import { ItemsTab } from './ItemsTab.js';
import { CargoComplexFiles } from './CargoComplexFiles.js';

const dtStyle = { fontWeight: 500, color: 'var(--fg-3)', fontSize: 13 };
const ddStyle = { margin: 0, fontSize: 13 };
const sectionLabelStyle = {
  fontSize: 10,
  letterSpacing: '.1em',
  textTransform: 'uppercase' as const,
  fontWeight: 600,
  color: 'var(--fg-3)',
};

function fmtNumber(value: number | null): string {
  return value === null ? '—' : String(value);
}

function CargoHeader({ request }: { request: RussiaRequest }) {
  const { t } = useTranslation('russiaRequests');
  return (
    <dl
      style={{
        display: 'grid',
        gridTemplateColumns: 'max-content 1fr',
        gap: '8px 16px',
        margin: 0,
      }}
      data-testid="russia-request-drawer-cargo-header"
    >
      <dt style={dtStyle}>{t('columns.type')}</dt>
      <dd style={ddStyle}>
        <RussiaRequestTypeChip type={request.type} />
      </dd>

      <dt style={dtStyle}>{t('columns.status')}</dt>
      <dd style={ddStyle}>
        <RequestStatusPill status={request.status} size="sm" />
      </dd>

      <dt style={dtStyle}>{t('columns.name')}</dt>
      <dd style={ddStyle}>{request.name}</dd>

      <dt style={dtStyle}>{t('columns.startCities')}</dt>
      <dd style={ddStyle}>
        <StartCityChips
          startCities={request.startCities}
          testIdPrefix="russia-request-drawer-cargo-start-cities"
        />
      </dd>

      <dt style={dtStyle}>{t('columns.endAddress')}</dt>
      <dd style={ddStyle}>{request.endAddress}</dd>
    </dl>
  );
}

function CargoComplexTotals({ request }: { request: RussiaRequest }) {
  const { t } = useTranslation('russiaRequests');
  return (
    <dl
      style={{
        display: 'grid',
        gridTemplateColumns: 'max-content 1fr',
        gap: '8px 16px',
        margin: 0,
      }}
      data-testid="russia-request-drawer-cargo-totals"
    >
      <dt style={dtStyle}>{t('drawer.cargo.totals.weightKg')}</dt>
      <dd style={ddStyle}>{fmtNumber(request.totalWeight)}</dd>
      <dt style={dtStyle}>{t('drawer.cargo.totals.volumeM3')}</dt>
      <dd style={ddStyle}>{fmtNumber(request.totalVolume)}</dd>
    </dl>
  );
}

export function CargoTab({ request }: { request: RussiaRequest }) {
  const { t } = useTranslation('russiaRequests');
  const isComplex = request.kind === RussiaRequestKind.COMPLEX;
  return (
    <div style={{ display: 'grid', gap: 18 }} data-testid="russia-request-drawer-cargo">
      <CargoHeader request={request} />
      <div style={{ display: 'grid', gap: 10 }}>
        <span style={sectionLabelStyle}>{t('drawer.cargo.section.body')}</span>
        {isComplex ? (
          <>
            <CargoComplexTotals request={request} />
            <CargoComplexFiles requestId={request.id} />
          </>
        ) : (
          <ItemsTab request={request} />
        )}
      </div>
    </div>
  );
}
